import { useEffect, useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import { useDispatch, useSelector } from 'react-redux';

import { APP_NAME, POOL_NAME } from '@/constants';
import { refreshTokenConfig } from '@/oidcConfig';
import { fetchAuditableElements } from '@/slices/auditableElementsSlice';
import { clearOnLogOut, refreshSign, refreshSignIn } from '@/slices/authSlice';
import { AppDispatch, RootState } from '@/store';

const REFRESH_IN_FLIGHT_GUARD_MS = 5000;

/**
 * Ensures Redux auth stays fresh when the access token is near expiry.
 * Mounted at AppSession so it still runs while the refresh loader is shown.
 */
const useShouldRefresh = () => {
  const dispatch: AppDispatch = useDispatch();
  const auth = useSelector((state: RootState) => state.auth);
  const { refreshToken, username, isOidcClient, poolName } = auth;
  const { needRefresh } = refreshTokenConfig(auth);
  const { signinSilent, signoutSilent } = useAuth();
  const inFlightRef = useRef(false);

  useEffect(() => {
    const handleRefresh = async () => {
      if (!needRefresh) return;
      if (auth?.status?.message === 'loading') return;
      if (inFlightRef.current) return;

      inFlightRef.current = true;
      const releaseTimer = window.setTimeout(() => {
        inFlightRef.current = false;
      }, REFRESH_IN_FLIGHT_GUARD_MS);

      try {
        if (isOidcClient) {
          const user = await signinSilent();
          if (user?.access_token) {
            dispatch(
              refreshSignIn({
                accessToken: user.access_token,
                idToken: user.id_token,
                refreshToken: user.refresh_token,
                expiresIn: user.expires_at
              })
            );
            dispatch(
              fetchAuditableElements({
                applicationName: APP_NAME
              })
            );
          } else {
            await Promise.allSettled([
              signoutSilent({ id_token_hint: auth.idToken }),
              dispatch(clearOnLogOut())
            ]);
            localStorage.clear();
          }
        } else {
          if (!refreshToken || !username) {
            dispatch(clearOnLogOut());
            return;
          }
          await dispatch(
            refreshSign({
              refreshToken,
              username,
              poolName: poolName || POOL_NAME
            })
          );
        }
      } catch (error: unknown) {
        console.error('Error refreshing token', error);
        try {
          if (isOidcClient) {
            await signoutSilent({ id_token_hint: auth.idToken });
          }
        } catch {
          // ignore silent sign-out failures during forced cleanup
        }
        dispatch(clearOnLogOut());
        localStorage.clear();
      } finally {
        window.clearTimeout(releaseTimer);
        inFlightRef.current = false;
      }
    };

    void handleRefresh();
  }, [
    auth.idToken,
    auth?.status?.message,
    dispatch,
    isOidcClient,
    needRefresh,
    poolName,
    refreshToken,
    signinSilent,
    signoutSilent,
    username
  ]);
};

export default useShouldRefresh;
